import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import CsvImportLogsTable from '../../components/csvImportLogs/CsvImportLogsTable';
import {
  fetchAllCsvImportLogsAction,
  fetchCsvImportLogsStatsAction,
  fetchCsvImportLogsByDateRangeAction
} from '../../redux/actions/csvImportLogs/csvImportLogsAction';

const CsvImportLogsPage = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.login.user); // Add this line
  const {
    allLogs,
    allLogsLoading,
    allLogsError,
    stats,
    statsLoading,
    statsError
  } = useSelector((state) => state.csvImportLogs);

  // Debug: Log the activeLogs state to help diagnose empty table issue
  console.log('DEBUG: activeLogs from Redux:', allLogs);

  const [filteredLogs, setFilteredLogs] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const hasInitialized = useRef(false);

  const loadData = useCallback(async () => {
    try {
      await Promise.all([
        dispatch(fetchAllCsvImportLogsAction()),
        dispatch(fetchCsvImportLogsStatsAction())
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  }, [dispatch]);

  useEffect(() => {
    if (!hasInitialized.current) {
      loadData();
      hasInitialized.current = true;
    }
  }, [loadData]);

  useEffect(() => {
    // Filter out deleted logs (is_deleted === 1)
    setFilteredLogs(allLogs.filter(log => !log.is_deleted));
  }, [allLogs]);

  const handleRefresh = async () => {
    try {
      await loadData();
      toast.success('Data refreshed successfully');
    } catch (error) {
      toast.error('Failed to refresh data');
    }
  };

  const handleDateRangeFilter = async () => {
    if (!startDate || !endDate) {
      toast.error('Please select both start and end dates');
      return;
    }

    try {
      await dispatch(fetchCsvImportLogsByDateRangeAction(startDate, endDate));
      toast.success('Date range filter applied');
    } catch (error) {
      toast.error('Failed to apply date range filter');
    }
  };

  const handleClearFilters = () => {
    setStartDate('');
    setEndDate('');
    setStatusFilter('all');
    setFilteredLogs(allLogs.filter(log => !log.is_deleted));
    toast.success('Filters cleared');
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    // Always filter out deleted logs
    const nonDeletedLogs = allLogs.filter(log => !log.is_deleted);
    if (status === 'all') {
      setFilteredLogs(nonDeletedLogs);
    } else {
      const filtered = nonDeletedLogs.filter(log => {
        if (status === 'confirmed') return log.is_confirm === 1;
        if (status === 'pending') return log.is_confirm === 0;
        return true;
      });
      setFilteredLogs(filtered);
    }
  };

  const StatCard = ({ title, value, color, loading }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography color="textSecondary" gutterBottom variant="h6">
          {title}
        </Typography>
        {loading ? (
          <CircularProgress size={20} />
        ) : (
          <Typography variant="h4" component="div" color={color}>
            {value || 0}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          CSV Import Logs
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          disabled={allLogsLoading}
        >
          Refresh
        </Button>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Imports"
            value={stats?.total_imports}
            color="primary.main"
            loading={statsLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Saved"
            value={stats?.total_saved}
            color="success.main"
            loading={statsLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Not Saved"
            value={stats?.total_not_saved}
            color="error.main"
            loading={statsLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Confirmed Imports"
            value={stats?.confirmed_imports}
            color="warning.main"
            loading={statsLoading}
          />
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterIcon />
          Filters
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => handleStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="confirmed">Confirmed</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleDateRangeFilter}
              disabled={!startDate || !endDate}
            >
              Apply Filter
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={handleClearFilters}
            >
              Clear
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Error Alert */}
      {(allLogsError || statsError) && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {allLogsError || statsError}
        </Alert>
      )}

      {/* Logs Table */}
      <Paper sx={{ p: 3 }}>
        <CsvImportLogsTable
          logs={filteredLogs}
          loading={allLogsLoading}
          onRefresh={loadData}
          currentUser={user}
        />
      </Paper>
    </Box>
  );
};

export default CsvImportLogsPage; 